import React from 'react';

import classNames from 'classnames';

import type {
    OpenGraphMetadata,
    Post,
    PostImage,
} from 'mattermost-redux/types/posts';

import SizeAwareImage from 'components/size_aware_image';
import ExternalImage from 'components/external_image';
import {PostTypes} from 'utils/constants';
import {isSystemMessage} from 'utils/post_utils';
import {makeUrlSafe} from 'utils/url';

import {getBestImageUrl} from './get_best_image_url';

export type Props = {
    postId: string;
    link: string;
    currentUserId?: string;
    post: Post;
    openGraphData?: OpenGraphMetadata;
    enableLinkPreviews?: boolean;
    previewEnabled?: boolean;
    isEmbedVisible?: boolean;
    toggleEmbedVisibility: () => void;
    actions: {
        editPost: (post: { id: string; props: Record<string, any> }) => void;
    };
    isInPermalink?: boolean;
};

export default class PostAttachmentOpenGraph extends React.PureComponent<Props> {
    getImageMetadata = (imageUrl?: string) => {
        if (!imageUrl) {
            return undefined;
        }

        const imagesMetadata = this.props.post.metadata.images;
        if (!imagesMetadata) {
            return undefined;
        }

        return imagesMetadata[imageUrl];
    };

    isLargeImage = (dimensions?: PostImage) => {
        if (!dimensions) {
            return false;
        }

        const {height, width} = dimensions;

        const largeImageMinRatio = 16 / 9;
        const largeImageMinWidth = 150;

        const imageRatio = width / height;

        return width >= largeImageMinWidth && imageRatio >= largeImageMinRatio;
    };

    renderImageToggle() {
        if (this.props.isInPermalink) {
            return undefined;
        }

        return (
            <button
                className={'style--none post__embed-visibility color--link'}
                data-expanded={this.props.isEmbedVisible}
                aria-label='Toggle Embed Visibility'
                onClick={this.props.toggleEmbedVisibility}
            />
        );
    }

    renderLargeImage(imageUrl: string, imageMetadata?: PostImage) {
        if (!this.props.isEmbedVisible) {
            return undefined;
        }

        return (
            <ExternalImage
                src={imageUrl}
                imageMetadata={imageMetadata}
            >
                {(safeImageUrl: string) => (
                    <SizeAwareImage
                        className='attachment__image attachment__image--opengraph large_image'
                        src={safeImageUrl}
                        dimensions={imageMetadata}
                    />
                )}
            </ExternalImage>
        );
    }

    renderSmallImage(imageUrl: string, imageMetadata?: PostImage) {
        if (!this.props.isEmbedVisible) {
            return undefined;
        }

        return (
            <div className='attachment__image__container--opengraph'>
                <ExternalImage
                    src={imageUrl}
                    imageMetadata={imageMetadata}
                >
                    {(safeImageUrl: string) => (
                        <SizeAwareImage
                            className='attachment__image attachment__image--opengraph'
                            src={safeImageUrl}
                            dimensions={imageMetadata}
                        />
                    )}
                </ExternalImage>
            </div>
        );
    }

    truncateText(text?: string) {
        const maxLength = 300;
        const ellipsis = '...';

        if (text && text.length > maxLength) {
            return text.substring(0, maxLength - ellipsis.length) + ellipsis;
        }
        return text;
    }

    handleRemovePreview = async () => {
        const props = Object.assign({}, this.props.post.props);
        props[PostTypes.REMOVE_LINK_PREVIEW] = 'true';

        const patchedPost = {
            id: this.props.post.id,
            file_ids: this.props.post.file_ids,
            props,
        };

        return this.props.actions.editPost(patchedPost);
    };

    hasPreviewBeenRemoved() {
        const {post} = this.props;

        if (!post || !post.props) {
            return false;
        }

        return post.props[PostTypes.REMOVE_LINK_PREVIEW] === 'true';
    }

    render() {
        if (!this.props.previewEnabled || !this.props.enableLinkPreviews) {
            return null;
        }

        if (!this.props.post || isSystemMessage(this.props.post)) {
            return null;
        }

        if (this.hasPreviewBeenRemoved()) {
            return null;
        }

        const data = this.props.openGraphData;
        if (!data) {
            return null;
        }

        const permalinkClass = this.props.isInPermalink ? 'permalink--opengraph' : '';

        const imageUrl = getBestImageUrl(data, this.props.post.metadata.images);
        const imageMetadata = this.getImageMetadata(imageUrl);
        const hasLargeImage = this.isLargeImage(imageMetadata);

        let removePreviewButton;
        if (this.props.currentUserId === this.props.post.user_id && !this.props.isInPermalink) {
            removePreviewButton = (
                <button
                    type='button'
                    className='btn-close'
                    aria-label='Close'
                    onClick={this.handleRemovePreview}
                    data-testid='removeLinkPreviewButton'
                >
                    <span aria-hidden='true'>{'×'}</span>
                </button>
            );
        }

        let body;
        if (data.description || imageUrl) {
            body = (
                <div className={'attachment__body attachment__body--opengraph'}>
                    <div className={classNames({'permalink__body--opengraph': this.props.isInPermalink})}>
                        {this.truncateText(data.description)}
                        {' '}
                        {imageUrl && hasLargeImage && this.renderImageToggle()}
                    </div>
                    {!this.props.isInPermalink && imageUrl && hasLargeImage && this.renderLargeImage(imageUrl, imageMetadata)}
                </div>
            );
        }

        return (
            <div className='attachment attachment--opengraph'>
                <div className={classNames('attachment__content', permalinkClass)}>
                    <div className={classNames('clearfix', 'attachment__container', 'attachment__container--opengraph', permalinkClass)}>
                        <div className={'attachment__body__wrap attachment__body__wrap--opengraph'}>
                            {!this.props.isInPermalink && <span className='sitename'>
                                {this.truncateText(data.site_name)}
                            </span>}
                            {removePreviewButton}
                            <h1 className={'attachment__title attachment__title--opengraph' + (data.title ? '' : ' is-url')}>
                                <a
                                    className='attachment__title-link attachment__title-link--opengraph'
                                    href={makeUrlSafe(data.url || this.props.link)}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    title={data.title || data.url || this.props.link}
                                >
                                    {this.truncateText(data.title || data.url || this.props.link)}
                                </a>
                            </h1>
                            {body}
                        </div>
                        {!this.props.isInPermalink && imageUrl && !hasLargeImage && this.renderSmallImage(imageUrl, imageMetadata)}
                    </div>
                </div>
            </div>
        );
    }
}
